import { PropsWithChildren, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  makeStyles,
} from '@material-ui/core';
import MenuIcon from '@material-ui/icons/Menu';
import MenuOpenIcon from '@material-ui/icons/MenuOpen';
import SearchIcon from '@material-ui/icons/Search';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import SettingsOutlinedIcon from '@material-ui/icons/SettingsOutlined';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import { useSidebarOpenState } from '@backstage/core-components';
import {
  ProfileInfo,
  configApiRef,
  discoveryApiRef,
  fetchApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { SearchModal, useSearchModal } from '@backstage/plugin-search';

const POLL_MS = 60_000;

const useStyles = makeStyles(theme => {
  const dark = theme.palette.type === 'dark';
  const border = dark ? 'rgba(255,255,255,0.07)' : 'rgba(17,24,39,0.08)';
  return {
    shell: {
      flex: 1,
      alignSelf: 'stretch',
      width: '100%',
      minHeight: 0,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      background: dark ? '#12151c' : '#ffffff',
      fontFamily: theme.typography.fontFamily,
    },
    brand: {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: '14px 10px 12px 14px',
    },
    brandRail: {
      flexDirection: 'column',
      gap: 6,
      padding: '10px 0 7px',
      borderBottom: `1px solid ${border}`,
    },
    brandLink: {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      minWidth: 0,
      flex: 1,
      textDecoration: 'none',
      color: 'inherit',
    },
    tile: {
      flexShrink: 0,
      width: 36,
      height: 36,
      borderRadius: 10,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: dark
        ? 'linear-gradient(145deg, #2a2140 0%, #1a1626 100%)'
        : 'linear-gradient(145deg, #f4edf8 0%, #e9ddf1 100%)',
      border: `1px solid ${
        dark ? 'rgba(183,148,246,0.25)' : 'rgba(110,58,143,0.18)'
      }`,
      boxShadow: dark ? '0 4px 14px rgba(110,58,143,0.35)' : 'none',
      '& img': { width: 24, height: 24, objectFit: 'contain' },
    },
    tileRail: {
      width: 42,
      height: 42,
      borderRadius: 12,
      '& img': { width: 27, height: 27 },
    },
    brandText: { minWidth: 0 },
    brandTitle: {
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      fontSize: 14,
      fontWeight: 800,
      letterSpacing: '0.06em',
      color: dark ? '#ffffff' : '#212b36',
    },
    brandBadge: {
      fontSize: 9,
      fontWeight: 700,
      letterSpacing: '0.08em',
      padding: '2px 6px',
      borderRadius: 5,
      color: dark ? '#c4b5fd' : '#6e3a8f',
      background: dark ? 'rgba(139,92,246,0.18)' : 'rgba(110,58,143,0.1)',
      border: `1px solid ${
        dark ? 'rgba(139,92,246,0.4)' : 'rgba(110,58,143,0.25)'
      }`,
    },
    brandSub: {
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      marginTop: 3,
      fontSize: 11.5,
      color: dark ? '#8b93a7' : '#637381',
      whiteSpace: 'nowrap',
    },
    liveDot: {
      width: 6,
      height: 6,
      borderRadius: '50%',
      background: '#22c55e',
      boxShadow: '0 0 0 3px rgba(34,197,94,0.18)',
      flexShrink: 0,
    },
    toggle: {
      flexShrink: 0,
      padding: 6,
      borderRadius: 8,
      color: dark ? '#8b93a7' : '#637381',
      '& svg': { fontSize: 18 },
      '&:hover': {
        color: dark ? '#ffffff' : '#212b36',
        backgroundColor: dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
      },
    },
    toggleRail: {
      width: 38,
      height: 30,
      padding: 0,
      border: `1px solid ${border}`,
      background: dark ? '#181c25' : '#f6f7f9',
    },
    searchWrap: {
      padding: '2px 12px 6px',
    },
    searchWrapRail: {
      padding: '9px 0 7px',
      display: 'flex',
      justifyContent: 'center',
    },
    search: {
      width: '100%',
      height: 36,
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: '0 8px 0 10px',
      borderRadius: 9,
      border: `1px solid ${border}`,
      background: dark ? '#181c25' : '#f6f7f9',
      color: dark ? '#7d8597' : '#8a94a6',
      fontFamily: 'inherit',
      fontSize: 12.5,
      cursor: 'pointer',
      transition: 'border-color 0.15s ease',
      '&:hover': {
        borderColor: dark ? 'rgba(183,148,246,0.45)' : 'rgba(110,58,143,0.35)',
      },
      '& svg': { fontSize: 17 },
    },
    searchRail: {
      width: 40,
      height: 40,
      margin: '0 auto',
      justifyContent: 'center',
      padding: 0,
      borderRadius: 11,
      '& svg': { fontSize: 20 },
    },
    searchText: { flex: 1, textAlign: 'left' },
    kbd: {
      fontFamily: '"JetBrains Mono", ui-monospace, monospace',
      fontSize: 10,
      padding: '2px 6px',
      borderRadius: 5,
      border: `1px solid ${border}`,
      background: dark ? '#12151c' : '#ffffff',
      color: dark ? '#9aa3b5' : '#637381',
    },
    nav: {
      flex: 1,
      minHeight: 0,
      overflowY: 'auto',
      overflowX: 'hidden',
      paddingBottom: 8,
      scrollbarWidth: 'none',
      '&::-webkit-scrollbar': { display: 'none' },
    },
    navRail: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '100%',
    },
    footer: {
      flexShrink: 0,
      padding: '10px 12px 12px',
      borderTop: `1px solid ${border}`,
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
    },
    footerRail: {
      padding: '9px 0 10px',
      alignItems: 'center',
      gap: 7,
    },
    status: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      height: 34,
      padding: '0 10px',
      borderRadius: 9,
      border: `1px solid ${border}`,
      background: dark ? '#161a22' : '#f9fafb',
      fontSize: 12,
      fontWeight: 500,
      color: dark ? '#d5dae3' : '#3a4452',
    },
    statusRail: {
      justifyContent: 'center',
      padding: 0,
      width: 38,
      height: 34,
      margin: '0 auto',
    },
    statusText: {
      flex: 1,
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    statusMeta: {
      fontFamily: '"JetBrains Mono", ui-monospace, monospace',
      fontSize: 10.5,
      color: dark ? '#7d8597' : '#8a94a6',
    },
    user: {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: '2px 0 0 2px',
    },
    userRail: { justifyContent: 'center', padding: 0 },
    avatar: {
      flexShrink: 0,
      width: 32,
      height: 32,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 12,
      fontWeight: 700,
      color: '#ffffff',
      background: 'linear-gradient(135deg, #8b5cf6 0%, #6e3a8f 100%)',
      overflow: 'hidden',
      cursor: 'pointer',
      border: 'none',
      padding: 0,
      '& img': { width: '100%', height: '100%', objectFit: 'cover' },
    },
    avatarRail: {
      width: 38,
      height: 38,
      border: `2px solid ${dark ? '#242a35' : '#eef0f3'}`,
      boxShadow: `0 0 0 1px ${border}`,
    },
    userText: { flex: 1, minWidth: 0 },
    userName: {
      fontSize: 13,
      fontWeight: 600,
      color: dark ? '#ffffff' : '#212b36',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    userSub: {
      fontSize: 11,
      color: dark ? '#7d8597' : '#8a94a6',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    kebab: {
      padding: 4,
      color: dark ? '#7d8597' : '#8a94a6',
      '& svg': { fontSize: 18 },
    },
  };
});

const SidebarBrand = () => {
  const classes = useStyles();
  const { isOpen, setOpen } = useSidebarOpenState();
  const label = isOpen ? 'Contraer menú' : 'Expandir menú';
  return (
    <div className={`${classes.brand} ${isOpen ? '' : classes.brandRail}`}>
      <Link to="/" className={classes.brandLink} aria-label="Inicio">
        <span className={`${classes.tile} ${isOpen ? '' : classes.tileRail}`}>
          <img src="/images/isotipo-fluxem.png" alt="" />
        </span>
        {isOpen && (
          <span className={classes.brandText}>
            <span className={classes.brandTitle}>
              FLUXEM <span className={classes.brandBadge}>STAGE</span>
            </span>
            <span className={classes.brandSub}>
              <span className={classes.liveDot} />
              Developer Portal
            </span>
          </span>
        )}
      </Link>
      <Tooltip title={label} placement="right">
        <IconButton
          size="small"
          className={`${classes.toggle} ${isOpen ? '' : classes.toggleRail}`}
          onClick={() => setOpen(!isOpen)}
          aria-label={label}
        >
          {isOpen ? <MenuOpenIcon /> : <MenuIcon />}
        </IconButton>
      </Tooltip>
    </div>
  );
};

const SidebarSearch = () => {
  const classes = useStyles();
  const { isOpen } = useSidebarOpenState();
  const { state, toggleModal } = useSearchModal();
  const isMac =
    typeof navigator !== 'undefined' &&
    /Mac|iPhone|iPad/.test(navigator.platform);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        toggleModal();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [toggleModal]);

  const button = (
    <button
      type="button"
      className={`${classes.search} ${isOpen ? '' : classes.searchRail}`}
      onClick={toggleModal}
      aria-label="Buscar"
    >
      <SearchIcon />
      {isOpen && (
        <>
          <span className={classes.searchText}>Buscar recursos, APIs…</span>
          <span className={classes.kbd}>{isMac ? '⌘K' : 'Ctrl K'}</span>
        </>
      )}
    </button>
  );

  return (
    <div
      className={`${classes.searchWrap} ${
        isOpen ? '' : classes.searchWrapRail
      }`}
    >
      {isOpen ? (
        button
      ) : (
        <Tooltip title="Buscar" placement="right" arrow>
          {button}
        </Tooltip>
      )}
      <SearchModal {...state} toggleModal={toggleModal} />
    </div>
  );
};

function useBackendHealth() {
  const configApi = useApi(configApiRef);
  const [health, setHealth] = useState<{ ok: boolean; ms?: number }>();

  useEffect(() => {
    const url = `${configApi.getString(
      'backend.baseUrl',
    )}/.backstage/health/v1/readiness`;
    let cancelled = false;
    const check = async () => {
      const started = performance.now();
      try {
        const response = await fetch(url, { cache: 'no-store' });
        if (!cancelled) {
          setHealth({
            ok: response.ok,
            ms: Math.round(performance.now() - started),
          });
        }
      } catch {
        if (!cancelled) setHealth({ ok: false });
      }
    };
    check();
    const timer = setInterval(check, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [configApi]);

  return health;
}

function initials(name?: string): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return (parts[0][0] + (parts[1]?.[0] ?? '')).toUpperCase();
}

const SidebarFooter = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { isOpen } = useSidebarOpenState();
  const identityApi = useApi(identityApiRef);
  const health = useBackendHealth();
  const [profile, setProfile] = useState<ProfileInfo>({});
  const [userRef, setUserRef] = useState<string>();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  useEffect(() => {
    identityApi
      .getProfileInfo()
      .then(setProfile)
      .catch(() => undefined);
    identityApi
      .getBackstageIdentity()
      .then(identity => setUserRef(identity.userEntityRef))
      .catch(() => undefined);
  }, [identityApi]);

  const name = profile.displayName || userRef?.split('/').pop() || 'Usuario';
  const subtitle = profile.email || userRef || '';
  const statusOk = health?.ok !== false;
  const statusLabel = !health
    ? 'Verificando servicios…'
    : health.ok
    ? 'Todos los sistemas operativos'
    : 'Servicio degradado';
  const dotStyle = {
    background: !health ? '#94a3b8' : statusOk ? '#22c55e' : '#ef4444',
    boxShadow: `0 0 0 3px ${
      statusOk ? 'rgba(34,197,94,0.18)' : 'rgba(239,68,68,0.2)'
    }`,
  };

  const avatar = (
    <button
      type="button"
      className={`${classes.avatar} ${isOpen ? '' : classes.avatarRail}`}
      onClick={() => navigate('/settings')}
      aria-label="Configuración"
    >
      {profile.picture ? <img src={profile.picture} alt="" /> : initials(name)}
    </button>
  );

  return (
    <div className={`${classes.footer} ${isOpen ? '' : classes.footerRail}`}>
      <Tooltip title={isOpen ? '' : statusLabel} placement="right" arrow>
        <div
          className={`${classes.status} ${isOpen ? '' : classes.statusRail}`}
        >
          <span className={classes.liveDot} style={dotStyle} />
          {isOpen && (
            <>
              <span className={classes.statusText}>{statusLabel}</span>
              {health?.ms !== undefined && (
                <span className={classes.statusMeta}>{health.ms} ms</span>
              )}
            </>
          )}
        </div>
      </Tooltip>

      <div className={`${classes.user} ${isOpen ? '' : classes.userRail}`}>
        {isOpen ? (
          avatar
        ) : (
          <Tooltip title={name} placement="right" arrow>
            {avatar}
          </Tooltip>
        )}
        {isOpen && (
          <>
            <div className={classes.userText}>
              <div className={classes.userName}>{name}</div>
              <div className={classes.userSub}>{subtitle}</div>
            </div>
            <IconButton
              size="small"
              className={classes.kebab}
              onClick={e => setMenuAnchor(e.currentTarget)}
              aria-label="Opciones de usuario"
            >
              <MoreVertIcon />
            </IconButton>
          </>
        )}
      </div>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            navigate('/settings');
          }}
        >
          <SettingsOutlinedIcon fontSize="small" style={{ marginRight: 10 }} />
          Configuración
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            identityApi.signOut();
          }}
        >
          <ExitToAppIcon fontSize="small" style={{ marginRight: 10 }} />
          Cerrar sesión
        </MenuItem>
      </Menu>
    </div>
  );
};

export const FluxSidebarShell = ({ children }: PropsWithChildren<{}>) => {
  const classes = useStyles();
  const { isOpen } = useSidebarOpenState();
  return (
    <div className={classes.shell}>
      <SidebarBrand />
      <SidebarSearch />
      <nav
        className={`${classes.nav} ${isOpen ? '' : classes.navRail}`}
        aria-label="Navegación principal"
      >
        {children}
      </nav>
      <SidebarFooter />
    </div>
  );
};

/** Pending handoffs assigned to the signed-in user, refreshed on navigation. */
export function usePendingHandoffs(): number {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const identityApi = useApi(identityApiRef);
  const location = useLocation();
  const [count, setCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { userEntityRef } = await identityApi.getBackstageIdentity();
        const base = await discoveryApi.getBaseUrl('handoffs');
        const params = new URLSearchParams({
          status: 'pendiente_implementacion',
          assignedTo: userEntityRef,
          pageSize: '1',
        });
        const response = await fetchApi.fetch(`${base}/handoffs?${params}`);
        if (!response.ok) return;
        const body = await response.json();
        if (!cancelled) setCount(Number(body.total) || 0);
      } catch {
        // badge is best-effort
      }
    };
    load();
    const timer = setInterval(load, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [discoveryApi, fetchApi, identityApi, location.pathname]);

  return count;
}

export function useCatalogCount(kind: string): number | undefined {
  const catalogApi = useApi(catalogApiRef);
  const [count, setCount] = useState<number>();
  useEffect(() => {
    catalogApi
      .queryEntities({ filter: { kind }, limit: 1, fields: ['metadata.name'] })
      .then(result => setCount(result.totalItems))
      .catch(() => setCount(undefined));
  }, [catalogApi, kind]);
  return count;
}

/** Link to the signed-in user's first group, or the groups list. */
export function useMyGroupPath(): string {
  const identityApi = useApi(identityApiRef);
  const [path, setPath] = useState('/catalog?filters[kind]=group');
  useEffect(() => {
    identityApi
      .getBackstageIdentity()
      .then(identity => {
        const group = identity.ownershipEntityRefs.find(ref =>
          ref.toLowerCase().startsWith('group:'),
        );
        if (!group) return;
        const [, rest] = group.split(':');
        const [namespace, name] = rest.includes('/')
          ? rest.split('/')
          : ['default', rest];
        setPath(`/catalog/${namespace}/group/${name}`);
      })
      .catch(() => undefined);
  }, [identityApi]);
  return path;
}
